import { useState } from "react";
import Icon from "@/components/icon";
import { VitrinCategory } from "@/models/home/VitrinCategory";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer";
import { cn } from "@/lib/cn";
import { ELEMENT_TYPES } from "@/features/analytics";
import {
  categoryFacetCounts,
  type FacetBucket,
} from "@/models/search/SearchFacets";

interface Props {
  categories: VitrinCategory[];
  selectedCategoryIds: number[];
  onToggleCategory: (id: number) => void;
  onClearCategories: () => void;
  /**
   * Per-category counts from the response. Undefined when the API sent no
   * facets, in which case the list renders exactly as it always has.
   */
  categoryCounts?: FacetBucket<number>[];
}

/**
 * How many courses this category would give. A count of 0 is meaningful, not
 * missing: the category facet ignores the user's own category selection, so a
 * zero means the *other* active filters leave nothing here.
 */
function CategoryCount({ value }: { value?: number }) {
  if (typeof value !== "number") return null;
  return (
    <span className="ms-auto shrink-0 text-[11px] font-normal text-default-400">
      {value.toLocaleString("fa-IR")}
    </span>
  );
}

// Circular check indicator — filled when selected, hollow outline otherwise.
function CheckMark({ checked }: { checked: boolean }) {
  return checked ? (
    <Icon name="check-circle-fill" size={20} className="shrink-0 text-primary" />
  ) : (
    <span className="size-5 shrink-0 rounded-full border-2 border-default-300" />
  );
}

export default function CategoryFilterDrawer({
  categories,
  selectedCategoryIds,
  onToggleCategory,
  onClearCategories,
  categoryCounts,
}: Props) {
  const [open, setOpen] = useState(false);
  const counts = categoryFacetCounts(categoryCounts);
  // Which parents are expanded to reveal their children.
  const [expanded, setExpanded] = useState<number[]>([]);

  const toggleExpand = (id: number) =>
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const count = selectedCategoryIds.length;

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <button
          type="button"
          data-track-type={ELEMENT_TYPES.filterChip}
          data-track-id="category"
          className={cn(
            "flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium border whitespace-nowrap transition flex-shrink-0",
            count > 0
              ? "text-primary border-primary bg-primary/10"
              : "text-default-600 border-default-300 bg-background",
          )}
        >
          <Icon name="category" size={16} />
          دسته‌بندی
          {count > 0 && (
            <span className="flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-primary text-white text-[10px] font-bold">
              {count.toLocaleString("fa-IR")}
            </span>
          )}
        </button>
      </DrawerTrigger>

      <DrawerContent className="mt-24 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <DrawerTitle>دسته‌بندی‌ها</DrawerTitle>
          {count > 0 && (
            <button
              type="button"
              onClick={onClearCategories}
              className="text-xs font-medium text-danger"
            >
              حذف انتخاب‌ها
            </button>
          )}
        </div>

        {/* Portalled out of the filter bar, so it names its own section. */}
        <div
          className="-mx-4 max-h-[60vh] overflow-y-auto px-2 divide-y divide-default-100"
          data-track-section="category_filter"
        >
          {categories.map((category) => {
            const hasChildren =
              Array.isArray(category.children) && category.children.length > 0;
            const isExpanded = expanded.includes(category.id);
            const count = counts?.get(category.id);
            // Still selectable when empty — the user may be about to drop the
            // filter that emptied it — but it should not read as a live option.
            const isEmpty = count === 0;

            return (
              <div key={category.id}>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => onToggleCategory(category.id)}
                    className={cn(
                      "flex-1 flex items-center gap-3 p-3 text-right",
                      isEmpty && "opacity-45",
                    )}
                    data-track-type={ELEMENT_TYPES.filterChip}
                    data-track-id={category.id}
                    data-track-target="category"
                  >
                    <CheckMark
                      checked={selectedCategoryIds.includes(category.id)}
                    />
                    <span
                      className={cn(
                        "text-sm",
                        selectedCategoryIds.includes(category.id)
                          ? "font-semibold text-primary"
                          : "font-medium text-default-800",
                      )}
                    >
                      {category.name}
                    </span>
                    <CategoryCount value={count} />
                  </button>

                  {hasChildren && (
                    <button
                      type="button"
                      onClick={() => toggleExpand(category.id)}
                      aria-label="زیردسته‌ها"
                      className="p-3 text-default-400"
                    >
                      <Icon
                        name="chevron-left"
                        size={16}
                        className={cn(
                          "transition-transform duration-200",
                          isExpanded && "-rotate-90",
                        )}
                      />
                    </button>
                  )}
                </div>

                {hasChildren && isExpanded && (
                  <div className="pr-6 pb-1">
                    {category.children.map((child) => {
                      const childCount = counts?.get(child.id);
                      return (
                        <button
                          key={child.id}
                          type="button"
                          onClick={() => onToggleCategory(child.id)}
                          className={cn(
                            "w-full flex items-center gap-3 p-2.5 text-right",
                            childCount === 0 && "opacity-45",
                          )}
                          data-track-type={ELEMENT_TYPES.filterChip}
                          data-track-id={child.id}
                          data-track-target="category"
                        >
                          <CheckMark
                            checked={selectedCategoryIds.includes(child.id)}
                          />
                          <span
                            className={cn(
                              "text-sm",
                              selectedCategoryIds.includes(child.id)
                                ? "font-semibold text-primary"
                                : "text-default-600",
                            )}
                          >
                            {child.name}
                          </span>
                          <CategoryCount value={childCount} />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DrawerClose asChild>
          <button
            type="button"
            className="mt-4 w-full rounded-xl bg-primary py-3 text-sm font-bold text-white"
          >
            مشاهده نتایج
          </button>
        </DrawerClose>
      </DrawerContent>
    </Drawer>
  );
}
